import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Input,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
} from '@chakra-ui/react';
import { FiPlusCircle } from 'react-icons/fi';
import ReactSelect from 'react-select';
import { Link, useHistory, useLocation } from 'react-router-dom';

import {
  NonConformity,
  qualityControlService,
} from '../../../../services/qualityControlService';
import {
  FACTORY_SHORT_LABELS,
  FACTORY_OPTIONS,
  STATUS_COLOR_SCHEMES,
  STATUS_LABELS,
} from '../../../../utils/qualityControlConstants';
import {
  buildListStatusSearch,
  countNonConformityIndicators,
  filterFromIndicator,
  formatDateBR,
  IndicatorKey,
  indicatorFromFilter,
  isNonConformityOverdue,
  parseListStatusFilter,
} from '../utils';
import { FormLetterhead } from '../FormLetterhead';
import { IndicatorCards } from '../IndicatorCards';
import { PageShell } from '../PageShell';
import { STATUS_BADGE_STYLE } from '../statusBadgeStyle';
import { reactSelectStyles } from '../reactSelectStyles';
import { useDemoMode } from '../useDemoMode';

const STATUS_OPTIONS = [
  { value: 'open', label: 'Aberto' },
  { value: 'in_progress', label: 'Em Andamento' },
  { value: 'closed', label: 'Concluído' },
];

const NonConformities: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const demo = useDemoMode();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const [items, setItems] = useState<NonConformity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [openerOptions, setOpenerOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [openedBy, setOpenedBy] = useState('');
  const [factoryUnit, setFactoryUnit] = useState('');
  // Filtro inicial vem da URL (?status= / ?overdue=1), p.ex. de um card do
  // Dashboard.
  const [initialFilter] = useState(() =>
    parseListStatusFilter(location.search),
  );
  const [status, setStatus] = useState(initialFilter.status);
  const [onlyOverdue, setOnlyOverdue] = useState(initialFilter.onlyOverdue);
  const [openedFrom, setOpenedFrom] = useState('');
  const [openedTo, setOpenedTo] = useState('');

  useEffect(() => {
    qualityControlService
      .list({}, demo)
      .then(allItems => {
        const uniqueNames = Array.from(
          new Set(allItems.map(item => item.created_by_user_name)),
        ).sort((a, b) => a.localeCompare(b));
        setOpenerOptions(
          uniqueNames.map(name => ({ value: name, label: name })),
        );
      })
      .catch(() => {
        setOpenerOptions([]);
      });
  }, [demo]);

  // Status e "somente vencidas" NÃO vão para a API: os cards contam a lista
  // sem esses dois filtros (senão clicar em "Abertas" zeraria os outros
  // cards). Eles são aplicados abaixo, na tela, com a mesma regra do selo
  // "Vencida" — card e tabela sempre batem. A API não pagina.
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await qualityControlService.list(
        {
          ...(openedBy ? { openedBy } : {}),
          ...(factoryUnit
            ? { factoryUnit: factoryUnit as 'bovine' | 'swine' }
            : {}),
          ...(openedFrom ? { openedFrom } : {}),
          ...(openedTo ? { openedTo } : {}),
        },
        demo,
      );
      setItems(data);
    } catch {
      setError('Não foi possível carregar as não conformidades.');
    } finally {
      setLoading(false);
    }
  }, [demo, factoryUnit, openedBy, openedFrom, openedTo]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(
    () =>
      items
        .map(item => ({
          item,
          overdue: isNonConformityOverdue(item.status, item.due_date),
        }))
        .filter(({ item, overdue }) => {
          if (status && item.status !== status) return false;
          if (onlyOverdue && !overdue) return false;
          return true;
        }),
    [items, onlyOverdue, status],
  );

  const indicators = useMemo(
    () => countNonConformityIndicators(items),
    [items],
  );

  const activeCard = useMemo(
    () => indicatorFromFilter({ status, onlyOverdue }),
    [onlyOverdue, status],
  );

  // Clicar no card ativo remove o filtro; em outro card, troca o filtro.
  const handleSelectCard = useCallback(
    (key: IndicatorKey) => {
      const next =
        key === activeCard
          ? { status: '', onlyOverdue: false }
          : filterFromIndicator(key);

      setStatus(next.status);
      setOnlyOverdue(next.onlyOverdue);
    },
    [activeCard],
  );

  // Mantém o filtro de status na URL: F5 e "voltar" preservam o que o
  // usuário escolheu (inclusive vindo de um card do Dashboard).
  useEffect(() => {
    const search = buildListStatusSearch({ status, onlyOverdue }, demo);

    if (search !== location.search) {
      history.replace({ pathname: location.pathname, search });
    }
  }, [demo, history, location.pathname, location.search, onlyOverdue, status]);

  const goToDetail = useCallback(
    (itemId: string) => {
      history.push(`/quality-control/${itemId}${demo ? '?demo=1' : ''}`);
    },
    [demo, history],
  );

  return (
    <PageShell>
      <FormLetterhead demo={demo} />

      <Flex
        direction={{ base: 'column', lg: 'row' }}
        align={{ base: 'stretch', lg: 'flex-start' }}
        gridGap="4"
        mb="6"
      >
        <Box flex="1" order={{ base: 1, lg: 0 }}>
          {!error && !(loading && items.length === 0) && (
            <>
              <IndicatorCards
                indicators={indicators}
                active={activeCard}
                onSelect={handleSelectCard}
              />
              <Text fontSize="xs" color="gray.400" mt="2">
                Totais conforme quem abriu, fábrica e período. Clique em um card
                para filtrar a lista; clique de novo para limpar.
              </Text>
            </>
          )}
        </Box>
        <Button
          as={Link}
          to={`/quality-control/new${demo ? '?demo=1' : ''}`}
          leftIcon={<FiPlusCircle />}
          colorScheme="blue"
          w={{ base: '100%', lg: 'auto' }}
          flexShrink={0}
          order={{ base: 0, lg: 1 }}
        >
          Registrar NC
        </Button>
      </Flex>

      <Box
        bg="gray.800"
        borderWidth="1px"
        borderColor="gray.700"
        borderRadius="lg"
        p="4"
        mb="6"
      >
        <Box
          display="grid"
          gridTemplateColumns={{ base: '1fr', md: '1fr 1fr 1fr' }}
          gridGap="3"
        >
          <Box>
            <Text fontSize="xs" color="gray.400" mb={1}>
              Quem abriu
            </Text>
            <ReactSelect
              placeholder="Todos"
              isClearable
              isSearchable
              options={openerOptions}
              value={
                openerOptions.find(option => option.value === openedBy) || null
              }
              onChange={option => setOpenedBy(option?.value || '')}
              styles={reactSelectStyles}
              noOptionsMessage={() => 'Nenhum nome encontrado'}
            />
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.400" mb={1}>
              Fábrica
            </Text>
            <Select
              size="sm"
              placeholder="Todas"
              value={factoryUnit}
              onChange={e => setFactoryUnit(e.target.value)}
            >
              {FACTORY_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.400" mb={1}>
              Status
            </Text>
            <Select
              size="sm"
              placeholder="Todos"
              value={status}
              onChange={e => setStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.400" mb={1}>
              Período de
            </Text>
            <Input
              size="sm"
              type="date"
              value={openedFrom}
              onChange={e => setOpenedFrom(e.target.value)}
            />
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.400" mb={1}>
              Período até
            </Text>
            <Input
              size="sm"
              type="date"
              value={openedTo}
              onChange={e => setOpenedTo(e.target.value)}
            />
          </Box>
          <Box>
            <Checkbox
              mt={{ base: '1', md: '7' }}
              isChecked={onlyOverdue}
              onChange={e => setOnlyOverdue(e.target.checked)}
            >
              Somente vencidas
            </Checkbox>
          </Box>
        </Box>
      </Box>

      {loading && (
        <Flex justify="center" py="10">
          <Spinner />
        </Flex>
      )}

      {!loading && error && <Text color="red.400">{error}</Text>}

      {!loading && !error && isMobile && (
        <Flex direction="column" gridGap="3">
          {rows.map(({ item, overdue }) => (
            <Box
              key={item.id}
              as="button"
              type="button"
              textAlign="left"
              w="100%"
              bg="gray.800"
              borderWidth="1px"
              borderColor="gray.700"
              borderRadius="lg"
              p="4"
              _active={{ bg: 'gray.700' }}
              onClick={() => goToDetail(item.id)}
            >
              <Flex
                justify="space-between"
                align="center"
                gridGap="2"
                flexWrap="wrap"
                mb="2"
              >
                <Text fontWeight="600">{item.code}</Text>
                <Flex gridGap="2" align="center">
                  <Badge
                    colorScheme={STATUS_COLOR_SCHEMES[item.status]}
                    {...STATUS_BADGE_STYLE}
                  >
                    {STATUS_LABELS[item.status]}
                  </Badge>
                  {overdue && (
                    <Badge colorScheme="red" {...STATUS_BADGE_STYLE}>
                      Vencida
                    </Badge>
                  )}
                </Flex>
              </Flex>
              <Text mb="3" wordBreak="break-word">
                {item.title}
              </Text>
              <Box
                display="grid"
                gridTemplateColumns="1fr 1fr"
                gridGap="2"
                fontSize="sm"
              >
                <Box>
                  <Text fontSize="xs" color="gray.400">
                    Abertura
                  </Text>
                  <Text>{formatDateBR(item.opened_at)}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.400">
                    Prazo
                  </Text>
                  <Text>{formatDateBR(item.due_date)}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.400">
                    Fábrica
                  </Text>
                  <Text>{FACTORY_SHORT_LABELS[item.factory_unit]}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.400">
                    Responsável
                  </Text>
                  <Text wordBreak="break-word">{item.responsible}</Text>
                </Box>
              </Box>
            </Box>
          ))}
          {rows.length === 0 && (
            <Text py="6" textAlign="center" color="gray.400">
              Nenhuma não conformidade encontrada com os filtros atuais.
            </Text>
          )}
        </Flex>
      )}

      {!loading && !error && !isMobile && (
        <Box
          bg="gray.800"
          borderWidth="1px"
          borderColor="gray.700"
          borderRadius="lg"
          overflowX="auto"
        >
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Código</Th>
                <Th>Título</Th>
                <Th>Abertura</Th>
                <Th>Prazo</Th>
                <Th>Fábrica</Th>
                <Th>Status</Th>
                <Th>Responsável</Th>
                <Th>Aberto por</Th>
                <Th>Conclusão</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map(({ item, overdue }) => (
                <Tr
                  key={item.id}
                  cursor="pointer"
                  _hover={{ bg: 'gray.700' }}
                  onClick={() => goToDetail(item.id)}
                >
                  <Td fontWeight="600" whiteSpace="nowrap">
                    {item.code}
                  </Td>
                  <Td>{item.title}</Td>
                  <Td>{formatDateBR(item.opened_at)}</Td>
                  <Td>{formatDateBR(item.due_date)}</Td>
                  <Td>{FACTORY_SHORT_LABELS[item.factory_unit]}</Td>
                  <Td>
                    <Flex gridGap="2" align="center" flexWrap="wrap">
                      <Badge
                        colorScheme={STATUS_COLOR_SCHEMES[item.status]}
                        {...STATUS_BADGE_STYLE}
                      >
                        {STATUS_LABELS[item.status]}
                      </Badge>
                      {overdue && (
                        <Badge colorScheme="red" {...STATUS_BADGE_STYLE}>
                          Vencida
                        </Badge>
                      )}
                    </Flex>
                  </Td>
                  <Td>{item.responsible}</Td>
                  <Td>{item.created_by_user_name}</Td>
                  <Td>{formatDateBR(item.closed_at)}</Td>
                </Tr>
              ))}
              {rows.length === 0 && (
                <Tr>
                  <Td colSpan={9}>
                    <Text py="6" textAlign="center" color="gray.400">
                      Nenhuma não conformidade encontrada com os filtros atuais.
                    </Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      )}
    </PageShell>
  );
};

export default NonConformities;
